import React, { useEffect, useRef } from 'react';

const GenerativeCanvas = () => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    let animationFrameId;

    let width, height;
    let particles = [];
    // Increase density for "rich" look
    const PARTICLE_COUNT = 24000; 
    
    // Mouse state
    let mouse = { x: 0, y: 0, isActive: false };
    
    // 3D Simplex Noise approximation (Simplex is better than Perlin for this)
    // Ported/Simplified for JS
    const perm = new Uint8Array(512);
    const p = new Uint8Array(256);
    for(let i=0; i<256; i++) p[i] = i;
    for(let i=0; i<256; i++) {
        let r = i + ~~(Math.random()*(256-i));
        let t = p[i]; p[i] = p[r]; p[r] = t;
    }
    for(let i=0; i<512; i++) perm[i] = p[i & 255];

    const dot = (g, x, y, z) => g[0]*x + g[1]*y + g[2]*z;
    const grad3 = [[1,1,0],[-1,1,0],[1,-1,0],[-1,-1,0],
                   [1,0,1],[-1,0,1],[1,0,-1],[-1,0,-1],
                   [0,1,1],[0,-1,1],[0,1,-1],[0,-1,-1]];
                   
    const noise3D = (xin, yin, zin) => {
        let n0, n1, n2, n3; 
        const F3 = 1.0/3.0;
        const s = (xin+yin+zin)*F3;
        const i = Math.floor(xin+s);
        const j = Math.floor(yin+s);
        const k = Math.floor(zin+s);
        const G3 = 1.0/6.0;
        const t = (i+j+k)*G3;
        const X0 = i-t;
        const Y0 = j-t;
        const Z0 = k-t;
        const x0 = xin-X0;
        const y0 = yin-Y0;
        const z0 = zin-Z0;
        let i1, j1, k1;
        let i2, j2, k2;
        if(x0>=y0) {
            if(y0>=z0) { i1=1; j1=0; k1=0; i2=1; j2=1; k2=0; }
            else if(x0>=z0) { i1=1; j1=0; k1=0; i2=1; j2=0; k2=1; }
            else { i1=0; j1=0; k1=1; i2=1; j2=0; k2=1; }
        } else {
            if(y0<z0) { i1=0; j1=0; k1=1; i2=0; j2=1; k2=1; }
            else if(x0<z0) { i1=0; j1=1; k1=0; i2=0; j2=1; k2=1; }
            else { i1=0; j1=1; k1=0; i2=1; j2=1; k2=0; }
        }
        const x1 = x0 - i1 + G3;
        const y1 = y0 - j1 + G3;
        const z1 = z0 - k1 + G3;
        const x2 = x0 - i2 + 2.0*G3;
        const y2 = y0 - j2 + 2.0*G3;
        const z2 = z0 - k2 + 2.0*G3;
        const x3 = x0 - 1.0 + 3.0*G3;
        const y3 = y0 - 1.0 + 3.0*G3;
        const z3 = z0 - 1.0 + 3.0*G3;
        const ii = i & 255;
        const jj = j & 255;
        const kk = k & 255;
        const gi0 = perm[ii+perm[jj+perm[kk]]] % 12;
        const gi1 = perm[ii+i1+perm[jj+j1+perm[kk+k1]]] % 12;
        const gi2 = perm[ii+i2+perm[jj+j2+perm[kk+k2]]] % 12;
        const gi3 = perm[ii+1+perm[jj+1+perm[kk+1]]] % 12;
        let t0 = 0.6 - x0*x0 - y0*y0 - z0*z0;
        if(t0<0) n0 = 0.0;
        else { t0 *= t0; n0 = t0 * t0 * dot(grad3[gi0], x0, y0, z0); }
        let t1 = 0.6 - x1*x1 - y1*y1 - z1*z1;
        if(t1<0) n1 = 0.0;
        else { t1 *= t1; n1 = t1 * t1 * dot(grad3[gi1], x1, y1, z1); }
        let t2 = 0.6 - x2*x2 - y2*y2 - z2*z2;
        if(t2<0) n2 = 0.0;
        else { t2 *= t2; n2 = t2 * t2 * dot(grad3[gi2], x2, y2, z2); }
        let t3 = 0.6 - x3*x3 - y3*y3 - z3*z3;
        if(t3<0) n3 = 0.0;
        else { t3 *= t3; n3 = t3 * t3 * dot(grad3[gi3], x3, y3, z3); }
        return 32.0*(n0 + n1 + n2 + n3);
    };

    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;
      initParticles();
    };

    class Particle {
      constructor() {
        this.reset();
        // Scatter initially
        this.x = Math.random() * width;
        this.y = Math.random() * height;
        this.z = Math.random() * 200;
      }

      reset() {
        // Start from random edges or center based on flow
        if (Math.random() > 0.5) {
             this.x = Math.random() * width;
             this.y = Math.random() > 0.5 ? -10 : height + 10;
        } else {
             this.x = Math.random() > 0.5 ? -10 : width + 10;
             this.y = Math.random() * height;
        }
        
        this.z = Math.random() * 100; // Depth
        this.vx = 0;
        this.vy = 0;
        this.vz = 0;
        this.life = Math.random() * 500 + 100;
        this.maxLife = this.life;
        this.baseSize = Math.random() * 1.5 + 0.5;
      }

      update(time) {
        // 3D Noise Flow Field
        const scale = 0.0015;
        const timeScale = 0.0001;
        
        // Get noise value for direction
        const angle = noise3D(this.x * scale, this.y * scale, time * timeScale) * Math.PI * 4;
        const angleZ = noise3D(this.x * scale, this.y * scale, (time + 1000) * timeScale) * Math.PI;

        // Base velocity from noise
        const speed = 0.8;
        this.vx += Math.cos(angle) * 0.05 * speed;
        this.vy += Math.sin(angle) * 0.05 * speed;
        this.vz += Math.sin(angleZ) * 0.05 * speed;

        // Interaction: Swirl around mouse
        if (mouse.isActive) {
          const dx = mouse.x - this.x;
          const dy = mouse.y - this.y;
          const dist = Math.sqrt(dx*dx + dy*dy);
          const maxDist = 300;
          
          if (dist < maxDist) {
             const force = (maxDist - dist) / maxDist;
             // Swirl effect
             this.vx += dy * 0.005 * force;
             this.vy -= dx * 0.005 * force;
             // Attraction
             this.vx += dx * 0.0005 * force;
             this.vy += dy * 0.0005 * force;
          }
        }

        // Friction
        this.vx *= 0.96;
        this.vy *= 0.96;
        this.vz *= 0.96;

        this.x += this.vx;
        this.y += this.vy;
        this.z += this.vz;

        // Bounds wrapping
        if (this.x < -50 || this.x > width + 50 || 
            this.y < -50 || this.y > height + 50) {
            this.reset();
        }

        this.life--;
        if (this.life <= 0) this.reset();
      }

      draw() {
        // Perspective projection
        const fov = 400;
        const scale = fov / (fov + this.z);
        
        // Don't draw if behind camera or too far
        if (scale < 0) return;

        const x2d = (this.x - width/2) * scale + width/2;
        const y2d = (this.y - height/2) * scale + height/2;
        const size = this.baseSize * scale;
        
        // Opacity based on depth and life
        const lifeOp = Math.min(1, this.life / 100); // Fade out at end
        const alpha = Math.max(0, Math.min(0.8, scale * 0.8 * lifeOp));

        ctx.fillStyle = `rgba(0, 0, 0, ${alpha})`;
        ctx.beginPath();
        ctx.arc(x2d, y2d, size, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    const initParticles = () => {
      particles = [];
      for (let i = 0; i < PARTICLE_COUNT; i++) {
        particles.push(new Particle());
      }
    };

    let time = 0;
    const animate = () => {
      // Clear with trail effect for "richer" motion blur
      ctx.fillStyle = 'rgba(255, 255, 255, 0.3)'; // Slower fade = longer trails
      ctx.fillRect(0, 0, width, height);

      time += 1;
      particles.forEach(p => {
        p.update(time);
        p.draw();
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    const handleMouseMove = (e) => {
      mouse.x = e.clientX;
      mouse.y = e.clientY;
      mouse.isActive = true;
    };

    const handleMouseLeave = () => {
      mouse.isActive = false;
    };

    window.addEventListener('resize', resize);
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseleave', handleMouseLeave);

    resize();
    animate();

    return () => {
      window.removeEventListener('resize', resize);
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseleave', handleMouseLeave);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <canvas 
      ref={canvasRef} 
      className="fixed inset-0 w-full h-full pointer-events-none"
      style={{ zIndex: 0 }}
    />
  );
};

export default GenerativeCanvas;
